import React, { createContext, PropsWithChildren, SyntheticEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export type BlockNavigationContextProps = {
  isNavigationBlocked: () => boolean;
  blockNavigation: (key: string, message: string) => void;
  unblockNavigation: (key: string) => void;
  handler: (event: SyntheticEvent) => void;
};

const defaultHandler = () => {
  throw Error('No BlockNavigationContextProvider set');
};
export const BlockNavigationContext = createContext<BlockNavigationContextProps>({
  isNavigationBlocked: defaultHandler,
  blockNavigation: defaultHandler,
  unblockNavigation: defaultHandler,
  handler: defaultHandler,
});

export const BlockNavigationProvider = ({ children }: PropsWithChildren) => {
  const blockers = useRef<{ key: string; message: string }[]>([]);

  const { t } = useTranslation();
  const isNavigationBlocked = () => blockers.current.length > 0;
  const blockedHandler = (event: SyntheticEvent) => {
    if (isNavigationBlocked()) {
      // eslint-disable-next-line no-restricted-globals
      const accept = confirm(
        `${t('components.blockNavigationProvider.header')}\n${blockers.current
          .map(block => `\n•${block.message}`)
          .join('')}`,
      );
      if (accept) {
        blockers.current = [];
      } else {
        event.stopPropagation();
        event.preventDefault();
        event.nativeEvent.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
        event.nativeEvent.preventDefault();
      }
    }
  };

  return (
    <BlockNavigationContext.Provider
      value={{
        isNavigationBlocked,
        blockNavigation: (key: string, message: string) => {
          if (!blockers.current.some(blocker => blocker.key === key)) {
            blockers.current = [...blockers.current, { key, message }];
          }
        },
        unblockNavigation: (key: string) =>
          (blockers.current = blockers.current.filter(blocker => blocker.key !== key)),
        handler: blockedHandler,
      }}
    >
      {children}
    </BlockNavigationContext.Provider>
  );
};
